import React, { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { ImpersonationBanner } from './ImpersonationBanner'
import { AnnouncementBanner } from './AnnouncementBanner'

interface MainLayoutProps {
  children: ReactNode
}

export function MainLayout({ children }: MainLayoutProps): React.ReactElement {
  const { user, currentAccount, accounts, logout, selectAccount, impersonation } = useAuth()
  const navigate = useNavigate()
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleSwitchAccount = async (accountId: string) => {
    const result = await selectAccount(accountId)
    if (result.success) {
      setShowAccountMenu(false)
      navigate('/')
    }
  }

  const hasMultipleAccounts = accounts.length > 1

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Impersonation Banner */}
      {impersonation?.active && <ImpersonationBanner />}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo & Account */}
            <div className="flex items-center gap-6">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 52.32 52.29">
                    <rect className="fill-primary" y="-.02" width="52.32" height="52.32" rx="7.97" ry="7.97"/>
                    <g>
                      <path fill="currentColor" d="M20.39,26.26h-4.01l1.29-4.6,2.71,4.6h0ZM34.94,38.17l-12.84-21.54c-1.54-2.5-5.05-2.76-8.51-2.76l.02.02h-.38l-7.36,24.28h7.19l1.7-6.11h9.04l1.98,3.37c1.54,2.5,5.69,2.76,9.16,2.76"/>
                      <path fill="currentColor" d="M44.16,20.87c0-3.89-3.15-7.04-7.04-7.04h-14.11c2.23.43,3.09,2.57,4.73,5.11h8.52c1.07,0,1.93.86,1.93,1.93s-.86,1.93-1.93,1.93h-6.31l7.67,12.63c1.29,2.09,4.13,2.64,7.03,2.76h2.28l-6.66-11.02c2.3-1.16,3.9-3.53,3.9-6.29"/>
                    </g>
                  </svg>
                </div>
                <span className="text-xl font-bold text-secondary">VMS</span>
              </Link>

              {/* Account Selector */}
              {currentAccount && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAccountMenu(!showAccountMenu)}
                    disabled={!hasMultipleAccounts}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
                      ${hasMultipleAccounts
                        ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                        : 'border-transparent cursor-default'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold
                      ${currentAccount.account_type === 'group'
                        ? 'bg-secondary/10 text-secondary'
                        : 'bg-primary/10 text-primary'
                      }
                    `}>
                      {currentAccount.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-secondary">{currentAccount.name}</p>
                      {currentAccount.parent_name && (
                        <p className="text-xs text-grey">{currentAccount.parent_name}</p>
                      )}
                    </div>
                    {hasMultipleAccounts && (
                      <svg className="w-4 h-4 text-grey ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                      </svg>
                    )}
                  </button>

                  {/* Account Dropdown */}
                  {showAccountMenu && hasMultipleAccounts && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowAccountMenu(false)}
                      />
                      <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-medium text-grey uppercase tracking-wider">Switch Account</p>
                        </div>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {accounts.map(account => (
                            <button
                              key={account.id}
                              type="button"
                              disabled={account.id === currentAccount.id}
                              onClick={() => handleSwitchAccount(account.id)}
                              className={`w-full px-4 py-2 flex items-center gap-3 transition-colors ${
                                account.id === currentAccount.id
                                  ? 'bg-gray-50 cursor-default'
                                  : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                            >
                              <div className={`
                                w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0
                                ${account.account_type === 'group'
                                  ? 'bg-secondary/10 text-secondary'
                                  : 'bg-primary/10 text-primary'
                                }
                              `}>
                                {account.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="text-left min-w-0">
                                <p className="text-sm font-medium text-secondary truncate">{account.name}</p>
                                {account.parent_name && (
                                  <p className="text-xs text-grey truncate">{account.parent_name}</p>
                                )}
                              </div>
                              {account.id === currentAccount.id && (
                                <svg className="w-4 h-4 text-primary ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Side - User Menu */}
            <div className="flex items-center gap-4">
              {/* Notifications (placeholder) */}
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-grey hover:text-secondary"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-secondary">{user?.full_name || user?.email}</p>
                  </div>
                  <svg className="w-4 h-4 text-grey" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-secondary">{user?.full_name}</p>
                        <p className="text-xs text-grey truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setShowUserMenu(false)}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left text-grey hover:text-secondary"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                          </svg>
                          Profile
                        </button>
                        <Link
                          to="/settings"
                          onClick={() => setShowUserMenu(false)}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left text-grey hover:text-secondary"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Settings
                        </Link>
                      </div>
                      <div className="border-t border-gray-100 pt-1">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-red-50 transition-colors text-left text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Announcements */}
      <AnnouncementBanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  )
}
